import React from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

export default function CartDrawer({ isOpen, onClose, cartItems, onRemove, onUpdateQty, onCheckout }) {
  const subtotal = cartItems?.reduce((sum, item) => sum + item?.price * item?.qty, 0);
  const shipping = subtotal > 75 ? 0 : 8.99;
  const total = subtotal + shipping;

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40" style={{ background: 'rgba(0,48,87,0.5)' }} onClick={onClose} />
      )}
      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-sm z-50 flex flex-col shadow-2xl transition-transform duration-300"
        style={{
          background: 'var(--color-background)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-border)', background: 'var(--color-primary)' }}>
          <div className="flex items-center gap-2">
            <Icon name="ShoppingCart" size={20} color="var(--color-accent)" />
            <h2 className="font-heading font-semibold text-lg text-white">Your Cart</h2>
            {cartItems?.length > 0 && (
              <span className="w-5 h-5 rounded-full flex items-center justify-center font-caption text-xs font-bold"
                style={{ background: 'var(--color-accent)', color: 'var(--color-accent-foreground)' }}>
                {cartItems?.reduce((s, i) => s + i?.qty, 0)}
              </span>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.1)' }}>
            <Icon name="X" size={16} color="#FFFFFF" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {cartItems?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 py-12">
              <div className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'var(--color-muted)' }}>
                <Icon name="ShoppingCart" size={28} color="var(--color-muted-foreground)" />
              </div>
              <p className="font-caption text-center" style={{ color: 'var(--color-muted-foreground)' }}>
                Your cart is empty.<br />Start supporting women-owned businesses!
              </p>
            </div>
          ) : (
            cartItems?.map((item) => (
              <div key={item?.id} className="flex gap-3 p-3 rounded-xl border" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                  <Image src={item?.image} alt={item?.imageAlt} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-caption text-sm font-semibold line-clamp-1" style={{ color: 'var(--color-foreground)' }}>{item?.name}</h4>
                  <p className="font-caption text-xs mb-2" style={{ color: 'var(--color-accent)' }}>{item?.seller}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border rounded-md overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                      <button onClick={() => onUpdateQty(item?.id, item?.qty - 1)} className="w-7 h-7 flex items-center justify-center">
                        <Icon name="Minus" size={12} color="var(--color-foreground)" />
                      </button>
                      <span className="w-8 text-center font-data text-xs font-medium" style={{ color: 'var(--color-foreground)' }}>{item?.qty}</span>
                      <button onClick={() => onUpdateQty(item?.id, item?.qty + 1)} className="w-7 h-7 flex items-center justify-center">
                        <Icon name="Plus" size={12} color="var(--color-foreground)" />
                      </button>
                    </div>
                    <span className="font-data text-sm font-bold" style={{ color: 'var(--color-foreground)' }}>
                      ${(item?.price * item?.qty)?.toFixed(2)}
                    </span>
                  </div>
                </div>
                <button onClick={() => onRemove(item?.id)} className="flex-shrink-0 self-start p-1">
                  <Icon name="Trash2" size={14} color="var(--color-error)" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems?.length > 0 && (
          <div className="p-4 border-t space-y-3" style={{ borderColor: 'var(--color-border)' }}>
            <div className="space-y-1.5">
              <div className="flex justify-between font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                <span>Subtotal</span><span className="font-data">${subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                <span>Shipping</span>
                <span className="font-data">{shipping === 0 ? <span style={{ color: 'var(--color-success)' }}>FREE</span> : `$${shipping?.toFixed(2)}`}</span>
              </div>
              {subtotal < 75 && (
                <p className="font-caption text-xs" style={{ color: 'var(--color-accent)' }}>
                  Add ${(75 - subtotal)?.toFixed(2)} more for free shipping!
                </p>
              )}
              <div className="flex justify-between font-caption font-semibold text-base pt-2 border-t" style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}>
                <span>Total</span><span className="font-data">${total?.toFixed(2)}</span>
              </div>
            </div>
            <Button variant="default" fullWidth onClick={onCheckout} iconName="CreditCard" iconPosition="left">
              Checkout with Stripe
            </Button>
            <Button variant="outline" fullWidth onClick={onClose}>
              Continue Shopping
            </Button>
          </div>
        )}
      </div>
    </>
  );
}